import { useEffect, useId, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, ArrowRight, Building2, Check, Eye, EyeOff, KeyRound, Lock, Mail, TriangleAlert, User } from "lucide-react";
import { authClient } from "../lib/auth";
import { usePageTitle } from "../lib/page-title";
import { cleanBackupCode, cleanCode, friendlyError, withFriendlyError, type CodeKind } from "../lib/two-factor";
import { track } from "../lib/track";
import { Logo } from "../components/layout";
import { TRIAL_DAYS } from "@metrickle/schema/plans";
import { rememberPlanIntent } from "../lib/billing";
import { rememberInvite, useBillingMode } from "../lib/early-access";
import { Button, Checkbox, INVALID, IconButton, Spinner, cx } from "../components/ui";

const MIN_PASSWORD = 10;

// --- Frame ---------------------------------------------------------------------------------

function AuthFrame({
  title,
  subtitle,
  footer,
  panel,
  focusTitle,
  children,
}: {
  title: string;
  subtitle: ReactNode;
  footer: ReactNode;
  panel: { eyebrow: string; headline: ReactNode };
  /** Move focus to the title, for a view that replaces the form on the same page. */
  focusTitle?: boolean;
  children: ReactNode;
}) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  usePageTitle(title);
  useEffect(() => {
    if (focusTitle) titleRef.current?.focus();
  }, [focusTitle]);

  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="flex flex-col px-4 py-6 sm:px-10 sm:py-8">
        <a href="https://metrickle.com" className="self-start rounded-lg">
          <Logo className="text-[17px]" />
          <span className="sr-only"> home page</span>
        </a>
        <main className="flex flex-1 items-center justify-center py-12">
          <div className="auth-rise w-full max-w-[400px]">
            <h1 ref={titleRef} tabIndex={focusTitle ? -1 : undefined} className="text-[28px] leading-tight font-semibold tracking-[-0.02em] text-ink outline-none sm:text-[32px]">
              {title}
            </h1>
            <p className="mt-2 text-[15px] text-ink-2">{subtitle}</p>
            <div className="mt-8">{children}</div>
            <p className="mt-8 border-t border-line pt-6 text-center text-sm text-ink-2">{footer}</p>
          </div>
        </main>
        <p className="text-xs text-ink-3">A verdict on every release, for web, iOS and Android.</p>
      </div>
      <ProductPanel {...panel} />
    </div>
  );
}

// --- Brand panel ---------------------------------------------------------------------------

// Sample data: Quillmere's keyboard checkout case from docs/VOCABULARY.md ("The fictional company").
const ROWS = [
  { name: "No accessibility settings", value: 71, color: "var(--series-1)" },
  { name: "Keyboard navigation, before v4.12", value: 69, color: "var(--series-2)" },
  { name: "Keyboard navigation, since v4.12", value: 38, color: "var(--series-2)", worst: true },
];

function ProductPanel({ eyebrow, headline }: { eyebrow: string; headline: ReactNode }) {
  return (
    <aside aria-label="About Metrickle" className="hidden p-3 lg:block">
      <div className="auth-panel relative flex h-full flex-col overflow-hidden rounded-[28px] border border-line px-12 pt-16 pb-12 xl:px-16">
        <div className="relative max-w-md">
          <p className="text-[13px] font-medium tracking-wide text-accent-text uppercase">{eyebrow}</p>
          <p className="mt-3 text-[34px] leading-[1.1] font-semibold tracking-[-0.025em] text-balance text-ink xl:text-[40px]">{headline}</p>
        </div>

        {/* Example report, not real data: hidden from assistive tech, described by the headline. */}
        <div aria-hidden className="relative mt-12 flex flex-1 items-start justify-center">
          <div className="auth-rise auth-delay-1 w-full max-w-[460px] rounded-2xl border border-line bg-surface p-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_24px_48px_-24px_rgba(0,0,0,0.25)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-ink">Checkout · Success rate</p>
                <p className="mt-0.5 text-xs text-ink-3">quillmere.example · since v4.12</p>
              </div>
              <span className="shrink-0 rounded-md bg-surface-2 px-2 py-1 text-[11px] text-ink-2">Sample data · fictional company</span>
            </div>
            <ol className="mt-5 space-y-3.5">
              {ROWS.map((r, i) => (
                <li key={r.name}>
                  <span className={cx("text-xs", r.worst ? "font-semibold text-ink" : "text-ink-2")}>{r.name}</span>
                  <div className="mt-1">
                    <Bar value={r.value} color={r.color} delay={i * 90} />
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div className="auth-rise auth-delay-2 absolute -bottom-2 left-0 w-[300px] rounded-xl border border-line bg-surface p-3.5 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)] xl:-left-4">
            <div className="flex gap-2.5">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-2 text-warn">
                <TriangleAlert size={14} strokeWidth={2.25} />
              </span>
              <div>
                <p className="text-[13px] leading-snug font-medium text-ink">212 people didn’t finish checkout</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-2">
                  Since v4.12, focus skips the “Pay now” button, so people using keyboard navigation can’t reach it with Tab.
                </p>
              </div>
            </div>
          </div>

          <div className="auth-rise auth-delay-3 absolute right-0 bottom-20 rounded-xl border border-line bg-surface px-4 py-3 shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)] xl:-right-4">
            <p className="text-[11px] text-ink-3">Gap found</p>
            <p className="tabular mt-0.5 text-xl font-semibold tracking-tight text-ink">About £9.1k lost so far</p>
          </div>
        </div>

        <ul className="relative mt-14 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-ink-2">
          {["Web, iOS and Android", "Cookieless mode", "Aiming for WCAG 2.2 AA"].map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5">
              <Check size={14} strokeWidth={2.5} className="text-good" aria-hidden />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function Bar({ value, color, delay }: { value: number; color: string; delay: number }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
        <div className="auth-bar h-full rounded-full" style={{ width: `${value}%`, background: color, animationDelay: `${300 + delay}ms` }} />
      </div>
      <span className="tabular w-8 text-right text-[11px] text-ink-2">{value}%</span>
    </div>
  );
}

// --- Form parts ----------------------------------------------------------------------------

function AuthField({
  label,
  icon,
  hint,
  trailing,
  "aria-describedby": describedBy,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; icon: ReactNode; hint?: ReactNode; trailing?: ReactNode }) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-ink">
        {label}
      </label>
      <div className="group relative">
        <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-ink-3 transition-colors group-focus-within:text-accent-text" aria-hidden>
          {icon}
        </span>
        <input
          id={id}
          aria-describedby={[hint && hintId, describedBy].filter(Boolean).join(" ") || undefined}
          className={cx(
            "h-11 w-full rounded-xl border border-control bg-surface pl-10 text-[15px] text-ink shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[border-color,box-shadow] outline-none placeholder:text-ink-3 hover:border-ink-3 focus:border-accent focus:ring-4 focus:ring-accent-wash",
            INVALID,
            trailing ? "pr-12" : "pr-3.5",
            className,
          )}
          {...props}
        />
        {trailing && <span className="absolute inset-y-0 right-1.5 flex items-center">{trailing}</span>}
      </div>
      {hint && (
        <div id={hintId} className="mt-1.5 text-xs text-ink-3">
          {hint}
        </div>
      )}
    </div>
  );
}

function PasswordField({ label, hint, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode }) {
  const [visible, setVisible] = useState(false);
  return (
    <AuthField
      label={label}
      hint={hint}
      icon={<Lock size={16} />}
      type={visible ? "text" : "password"}
      trailing={
        <IconButton label="Show password" aria-pressed={visible} className="h-8 w-9" onClick={() => setVisible((v) => !v)}>
          {visible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
        </IconButton>
      }
      {...props}
    />
  );
}

function SubmitButton({ pending, children, pendingLabel }: { pending: boolean; children: ReactNode; pendingLabel: string }) {
  return (
    <button
      type="submit"
      disabled={pending}
      className="group relative mt-2 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-accent text-[15px] font-medium text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.15),0_1px_2px_rgba(0,0,0,0.12),0_8px_20px_-8px_var(--accent)] transition-[background-color,transform] hover:bg-accent-hover active:translate-y-px disabled:cursor-wait disabled:opacity-80"
    >
      {pending ? (
        <>
          <Spinner size={16} decorative current />
          {pendingLabel}
        </>
      ) : (
        <>
          {children}
          <ArrowRight size={16} aria-hidden className="transition-transform group-hover:translate-x-0.5" />
        </>
      )}
    </button>
  );
}

function FormError({ id, message }: { id?: string; message: string | null }) {
  const ref = useRef<HTMLDivElement>(null);
  // Move focus to the error so keyboard and screen-reader users land on it after a failed submit.
  useEffect(() => {
    if (message) ref.current?.focus();
  }, [message]);
  if (!message) return null;
  return (
    <div
      ref={ref}
      id={id}
      tabIndex={-1}
      role="alert"
      className="flex gap-2.5 rounded-xl border border-[color-mix(in_srgb,var(--bad)_35%,transparent)] bg-[color-mix(in_srgb,var(--bad)_8%,var(--surface))] px-3.5 py-3 text-sm text-ink outline-none"
    >
      <AlertCircle size={16} aria-hidden className="mt-0.5 shrink-0 text-bad" />
      <span>{message}</span>
    </div>
  );
}

/** Good news or a note above a form: an icon and a sentence, announced politely. */
function Notice({ icon = <Check size={18} strokeWidth={2.5} className="text-good" />, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div role="status" className="flex gap-3 rounded-xl border border-line bg-surface px-4 py-3.5 text-sm text-ink-2">
      <span aria-hidden className="mt-0.5 shrink-0">
        {icon}
      </span>
      <p>{children}</p>
    </div>
  );
}

/** Headed for a workspace invitation (/invite/:id), so the sign-in and sign-up pages say so. */
const isInvitation = (next: string | null) => !!next?.startsWith("/invite/");

// --- Submit ---------------------------------------------------------------------------------

function useSubmit(
  event: "login" | "signup",
  fn: () => Promise<{ data?: unknown; error?: { message?: string; code?: string } | null }>,
  /** The password was right and a second factor comes next. */
  onTwoFactor?: () => void,
  /** The account exists but its email isn't confirmed yet (a new account, or signing in before confirming). */
  onUnconfirmed?: () => void,
) {
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  return {
    error,
    pending,
    onSubmit: async (e: FormEvent) => {
      e.preventDefault();
      setPending(true);
      setError(null);
      let res: Awaited<ReturnType<typeof fn>>;
      try {
        res = await fn();
      } catch {
        res = { error: { message: "Can't reach the server. Check your connection and try again." } };
      }
      setPending(false);
      if (onUnconfirmed && res.error?.code === "EMAIL_NOT_VERIFIED") return onUnconfirmed();
      if (res.error) return setError(res.error.message ?? "Something went wrong on our side. Please try again in a moment.");
      if (onTwoFactor && (res.data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) return onTwoFactor();
      track(event, { method: "password" });
      // Signing up makes the account but no session until the email is confirmed.
      if (onUnconfirmed && !(res.data as { token?: string | null } | null)?.token) return onUnconfirmed();
      navigate(params.get("next") ?? "/", { replace: true });
    },
  };
}

/** Carries ?next= across the login/signup switch so the user still lands where they were headed. */
function useSwitchHref(path: string) {
  const [params] = useSearchParams();
  const next = params.get("next");
  return next ? `${path}?next=${encodeURIComponent(next)}` : path;
}

/** A step of signing in (the code, or single sign-on), keeping ?next=. */
function useStepHref(step: "code" | "sso") {
  const [params] = useSearchParams();
  const next = params.get("next");
  return `/login?${new URLSearchParams({ step, ...(next ? { next } : {}) })}`;
}

// Underlined: these links sit inside a sentence, so colour alone mustn't mark them (WCAG 1.4.1).
const linkClass = "font-medium text-accent-text underline underline-offset-4 hover:decoration-2";

/**
 * Where the emailed link lands. Confirming signs the person in, and /login then sends them on to
 * ?next=. A link that has expired or was already used comes back here with ?error= instead.
 */
function useVerifyCallback() {
  const [params] = useSearchParams();
  return `/login?${new URLSearchParams({ confirm: "1", next: params.get("next") ?? "/" })}`;
}

/** Why an emailed link didn't work, from the ?error= better-auth adds to the callback. */
function linkError(code: string | null): string | null {
  if (!code) return null;
  if (code.toUpperCase() === "TOKEN_EXPIRED") return "That confirmation link has expired. Sign in and we'll email you a new one.";
  return "That confirmation link didn't work. It may have been used already. Sign in and we'll email you a new one if you still need it.";
}

const RESEND_WAIT = 30;

/** After signing up, or signing in before confirming: the link is on its way. */
function CheckEmail({ email, reason, onBack }: { email: string; reason: "signup" | "signin"; onBack: () => void }) {
  const callbackURL = useVerifyCallback();
  const loginHref = useSwitchHref("/login");
  const [wait, setWait] = useState(RESEND_WAIT);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const resend = async () => {
    setPending(true);
    setStatus(null);
    const res = await authClient.sendVerificationEmail({ email, callbackURL }).catch(() => ({ error: { message: "" } }));
    setPending(false);
    if (res.error) return setStatus({ ok: false, text: "We couldn't send another email just now. Wait a minute and try again." });
    track("verification_resent", { reason });
    setWait(RESEND_WAIT);
    setStatus({ ok: true, text: `Sent. Check ${email} again.` });
  };

  return (
    <AuthFrame
      title="Check your email"
      focusTitle
      subtitle={
        <>
          {reason === "signin" ? "Confirm your email before signing in. We've sent a new link to " : "We've sent a link to "}
          <strong className="font-medium break-all text-ink">{email}</strong>. Open it to confirm your address and you're in.
        </>
      }
      footer={
        <>
          Already confirmed?{" "}
          <Link className={linkClass} to={loginHref} onClick={onBack}>
            Sign in
          </Link>
        </>
      }
      panel={{ eyebrow: "Set up in minutes", headline: "Know when a release costs you sign-ups or sales, and who it hit." }}
    >
      <div className="space-y-4">
        <div className="flex gap-3 rounded-xl border border-line bg-surface px-4 py-3.5 text-sm text-ink-2">
          <Mail size={18} aria-hidden className="mt-0.5 shrink-0 text-accent-text" />
          <p>The link works for 24 hours. It can take a minute to arrive, so check your spam or promotions folder too.</p>
        </div>
        <button
          type="button"
          onClick={resend}
          disabled={pending || wait > 0}
          aria-describedby={wait > 0 ? "resend-wait" : undefined}
          className="inline-flex h-11 w-full items-center justify-center rounded-xl border border-control bg-surface text-[15px] font-medium text-ink transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pending ? "Sending…" : "Send the email again"}
        </button>
        {wait > 0 && (
          <p id="resend-wait" className="text-center text-xs text-ink-3">
            You can ask for another in {wait} {wait === 1 ? "second" : "seconds"}.
          </p>
        )}
        <p role="status" className={cx("text-center text-sm", status?.ok ? "text-good" : "text-bad")}>
          {status?.text}
        </p>
        <Button type="button" variant="link" className="w-full justify-center text-sm underline-offset-4" onClick={onBack}>
          {reason === "signin" ? "Use a different account" : "Wrong address? Sign up again"}
        </Button>
      </div>
    </AuthFrame>
  );
}

// --- Google, GitHub and X ------------------------------------------------------------------

/** Mirrors SOCIAL_PROVIDERS in apps/api/src/auth.ts. better-auth calls X "twitter". */
type SocialProvider = "google" | "github" | "twitter";

const SOCIAL: Record<SocialProvider, { name: string; icon: ReactNode }> = {
  google: {
    name: "Google",
    icon: (
      <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden>
        <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
        <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
        <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
        <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
      </svg>
    ),
  },
  github: {
    name: "GitHub",
    icon: (
      <svg viewBox="0 0 16 16" width="18" height="18" fill="currentColor" aria-hidden>
        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
      </svg>
    ),
  },
  twitter: {
    name: "X",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
};

/** The providers switched on for this deployment (none if it can't tell, so no dead buttons). */
const useSocialProviders = () =>
  useQuery({
    queryKey: ["auth-providers"],
    queryFn: async () => {
      const res = await fetch("/api/auth-providers").catch(() => null);
      return res?.ok ? ((await res.json()) as { providers: SocialProvider[] }).providers : [];
    },
    staleTime: Infinity,
  });

/** Why a Google, GitHub or X sign-in came back without signing in, from the ?error= better-auth adds. */
function socialError(params: URLSearchParams): string | null {
  const provider = params.get("social") as SocialProvider | null;
  const code = params.get("error");
  if (!provider || !code) return null;
  const name = SOCIAL[provider]?.name ?? "that account";
  switch (code.toLowerCase()) {
    case "email_not_found":
      return `${name} didn't share a confirmed email address with us. Add or confirm one in your ${name} settings, or use another way to sign in.`;
    case "account_not_linked":
      return `There's already a Metrickle account for that email that isn't confirmed yet. Open the link we emailed you, or sign in with your password, then try ${name} again.`;
    case "access_denied":
      return `Signing in with ${name} was cancelled. Try again, or use another way to sign in.`;
    default:
      return `Signing in with ${name} didn't work. Try again, or use another way to sign in.`;
  }
}

/**
 * Google, GitHub and X. Coming back signed in lands on /login or /signup with ?done= and ?via=,
 * where RedirectIfSignedIn counts the login or signup and sends the person on to ?next=. A
 * refusal comes back to the same page with ?social= and ?error=.
 */
function SocialButtons({ from, onError }: { from: "login" | "signup"; onError: (message: string | null) => void }) {
  const providers = useSocialProviders();
  const [params] = useSearchParams();
  const [pending, setPending] = useState<SocialProvider | null>(null);
  const next = params.get("next") ?? "/";
  if (!providers.data?.length) return null;

  const start = async (provider: SocialProvider) => {
    setPending(provider);
    onError(null);
    const back = (extra: Record<string, string>) => `/${from}?${new URLSearchParams({ ...extra, next })}`;
    const res = await authClient.signIn
      .social({
        provider,
        callbackURL: back({ done: "login", via: provider }),
        newUserCallbackURL: back({ done: "signup", via: provider }),
        errorCallbackURL: back({ social: provider }),
      })
      .catch(() => ({ error: { message: "Can't reach the server. Check your connection and try again." } }));
    // On success the browser is already on its way to the provider: stay "pending".
    if (res.error) {
      setPending(null);
      onError(res.error.message ?? `Signing in with ${SOCIAL[provider].name} didn't work. Try again in a moment.`);
    }
  };

  return (
    <div className="space-y-3">
      {providers.data.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => start(p)}
          disabled={pending !== null}
          aria-busy={pending === p}
          className="inline-flex h-11 w-full items-center justify-center gap-2.5 rounded-xl border border-control bg-surface text-[15px] font-medium text-ink transition-colors hover:bg-surface-2 disabled:cursor-wait disabled:opacity-60"
        >
          {pending === p ? <Spinner size={16} decorative /> : SOCIAL[p].icon}
          {pending === p ? `Opening ${SOCIAL[p].name}…` : `Continue with ${SOCIAL[p].name}`}
        </button>
      ))}
    </div>
  );
}

function OrDivider({ children = "or" }: { children?: string }) {
  return (
    <div className="my-6 flex items-center gap-3 text-xs text-ink-3" aria-hidden>
      <span className="h-px flex-1 bg-line" />
      {children}
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

// --- Pages ----------------------------------------------------------------------------------

/** Password, then a code if two-factor is on. Or single sign-on, through the company's identity provider. */
export function Login() {
  const [params] = useSearchParams();
  const step = params.get("step");
  if (step === "code") return <CodeStep />;
  if (step === "sso") return <SsoStep />;
  return <PasswordStep />;
}

function PasswordStep() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [unconfirmed, setUnconfirmed] = useState(false);
  const navigate = useNavigate();
  const codeHref = useStepHref("code");
  const ssoHref = useStepHref("sso");
  const s = useSubmit(
    "login",
    () => authClient.signIn.email({ email, password }),
    () => navigate(codeHref),
    () => setUnconfirmed(true),
  );
  const signupHref = useSwitchHref("/signup");
  const fromLink = params.get("confirm") ? linkError(params.get("error")) : null;
  const [socialFail, setSocialFail] = useState<string | null>(() => socialError(params));
  const invitation = isInvitation(params.get("next"));
  if (unconfirmed) return <CheckEmail email={email} reason="signin" onBack={() => setUnconfirmed(false)} />;
  return (
    <AuthFrame
      title="Welcome back"
      subtitle={invitation ? "Sign in with the email address your invitation was sent to, and you can accept it." : "Sign in to see how your latest release is doing."}
      footer={
        <>
          New to Metrickle?{" "}
          <Link className={linkClass} to={signupHref}>
            Create an account
          </Link>
        </>
      }
      panel={{ eyebrow: "Release verdicts", headline: "Find the release that’s costing you conversions, and who it hit." }}
    >
      <form onSubmit={s.onSubmit} className="space-y-5" aria-busy={s.pending}>
        <FormError message={s.error ?? socialFail ?? fromLink} />
        {params.get("reset") && !s.error && <Notice>Your password has been changed. Sign in with your new password.</Notice>}
        <AuthField
          label="Email"
          icon={<Mail size={16} />}
          type="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          placeholder="you@company.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div>
          <PasswordField label="Password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          {/* The address goes along in router state, never the URL. */}
          <Link className={cx(linkClass, "mt-2 inline-block text-sm")} to="/forgot-password" state={{ email }}>
            Forgot your password?
          </Link>
        </div>
        <SubmitButton pending={s.pending} pendingLabel="Signing in…">
          Sign in
        </SubmitButton>
      </form>
      <OrDivider />
      <SocialButtons from="login" onError={setSocialFail} />
      <Link
        to={ssoHref}
        className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-control bg-surface text-[15px] font-medium text-ink transition-colors hover:bg-surface-2"
      >
        <Building2 size={16} aria-hidden className="text-ink-3" />
        Sign in with single sign-on (SSO)
      </Link>
    </AuthFrame>
  );
}

/** After the password, when two-factor sign-in is on. */
function CodeStep() {
  const [kind, setKind] = useState<CodeKind>("totp");
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const errorId = useId();
  const backHref = useSwitchHref("/login");
  const s = useSubmit("login", async () => {
    if (kind === "backup") return withFriendlyError(await authClient.twoFactor.verifyBackupCode({ code: cleanBackupCode(code), trustDevice: trust }));
    const clean = cleanCode(code);
    if (!/^\d{6}$/.test(clean)) return { error: { message: "Enter the 6-digit code from your authenticator app." } };
    return withFriendlyError(await authClient.twoFactor.verifyTotp({ code: clean, trustDevice: trust }));
  });
  const totp = kind === "totp";
  return (
    <AuthFrame
      title={totp ? "Enter your code" : "Enter a backup code"}
      subtitle={totp ? "Open your authenticator app and enter the 6-digit code for Metrickle." : "Use one of the backup codes you saved when you turned on two-factor sign-in. Each works once."}
      footer={
        <Link className={linkClass} to={backHref}>
          Back to sign in
        </Link>
      }
      panel={{ eyebrow: "Release verdicts", headline: "Find the release that’s costing you conversions, and who it hit." }}
    >
      <form onSubmit={s.onSubmit} className="space-y-5" aria-busy={s.pending}>
        <FormError id={errorId} message={s.error} />
        <AuthField
          key={kind}
          label={totp ? "6-digit code" : "Backup code"}
          icon={<KeyRound size={16} />}
          autoComplete="one-time-code"
          inputMode={totp ? "numeric" : "text"}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          required
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-invalid={s.error ? true : undefined}
          aria-describedby={s.error ? errorId : undefined}
          className="font-mono tracking-widest"
        />
        <Checkbox checked={trust} onCheckedChange={setTrust} hint="You won't need a code on this browser for 30 days. Don't use this on a shared computer.">
          Trust this device for 30 days
        </Checkbox>
        <SubmitButton pending={s.pending} pendingLabel="Checking…">
          Verify
        </SubmitButton>
      </form>
      <Button
        type="button"
        variant="link"
        className="mt-5 text-sm underline-offset-4"
        onClick={() => {
          setKind(totp ? "backup" : "totp");
          setCode("");
        }}
      >
        {totp ? "Use a backup code instead" : "Use your authenticator app instead"}
      </Button>
    </AuthFrame>
  );
}

/** Sends the person to their company's identity provider, found from their email's domain. */
function SsoStep() {
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  // Coming back from an identity provider that refused or failed (errorCallbackURL below).
  const returned = params.get("error");
  const [error, setError] = useState<string | null>(
    returned ? `Single sign-on didn't finish. Your company's sign-in said: ${params.get("error_description") ?? returned}. Try again, or ask your workspace admin.` : null,
  );
  const backHref = useSwitchHref("/login");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const res = await authClient.signIn
      .sso({ email, callbackURL: next, errorCallbackURL: `/login?${new URLSearchParams({ step: "sso", next })}` })
      .catch(() => ({ data: null, error: { message: "Can't reach the server. Check your connection and try again." } }));
    // On success the browser is already on its way to the identity provider: stay "pending".
    if (res.error) {
      setPending(false);
      setError(friendlyError(res.error));
    }
  };

  return (
    <AuthFrame
      title="Sign in with single sign-on"
      subtitle="Enter your work email. We'll take you to your company's sign-in page."
      footer={
        <Link className={linkClass} to={backHref}>
          Sign in with a password instead
        </Link>
      }
      panel={{ eyebrow: "Release verdicts", headline: "Find the release that’s costing you conversions, and who it hit." }}
    >
      <form onSubmit={onSubmit} className="space-y-5" aria-busy={pending}>
        <FormError message={error} />
        <AuthField
          label="Work email"
          icon={<Mail size={16} />}
          type="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          placeholder="you@company.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <SubmitButton pending={pending} pendingLabel="Opening your company's sign-in…">
          Continue
        </SubmitButton>
      </form>
    </AuthFrame>
  );
}

export function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const callbackURL = useVerifyCallback();
  const s = useSubmit("signup", () => authClient.signUp.email({ name, email, password, callbackURL }), undefined, () => setSent(true));
  // A plan picked on the pricing page (?plan=pro&interval=year) is offered again once the workspace exists.
  const [search] = useSearchParams();
  useEffect(() => {
    rememberPlanIntent(search);
    rememberInvite(search);
  }, [search]);
  const mode = useBillingMode();
  const invited = !!search.get("invite");
  const loginHref = useSwitchHref("/login");
  // The Sign up task starts here rather than at the /signup pageview: that pageview also lands
  // for signed-in visitors just before they're redirected, and they can never complete it.
  const session = authClient.useSession();
  const signedOut = !session.isPending && !session.data;
  const viewed = useRef(false);
  useEffect(() => {
    if (!signedOut || viewed.current) return;
    viewed.current = true;
    track("signup_viewed");
  }, [signedOut]);
  const longEnough = password.length >= MIN_PASSWORD;
  const [socialFail, setSocialFail] = useState<string | null>(() => socialError(search));
  const social = useSocialProviders();
  if (sent) return <CheckEmail email={email} reason="signup" onBack={() => setSent(false)} />;
  return (
    <AuthFrame
      title="Create your account"
      subtitle={
        isInvitation(search.get("next"))
          ? "Use the email address your invitation was sent to. Once you've confirmed it, you can accept the invitation."
          : mode.data?.open
          ? `Includes ${TRIAL_DAYS} days of Growth, free. No credit card needed.`
          : invited
            ? "You've been invited to Metrickle's early access. Growth is free while we launch."
            : "Start on Free, no card needed. During early access you can ask for Growth, free while we launch."
      }
      footer={
        <>
          Already have an account?{" "}
          <Link className={linkClass} to={loginHref}>
            Sign in
          </Link>
        </>
      }
      panel={{ eyebrow: "Set up in minutes", headline: "Know when a release costs you sign-ups or sales, and who it hit." }}
    >
      {socialFail && (
        <div className="mb-5">
          <FormError message={socialFail} />
        </div>
      )}
      <SocialButtons from="signup" onError={setSocialFail} />
      {!!social.data?.length && <OrDivider>or use your email</OrDivider>}
      <form onSubmit={s.onSubmit} className="space-y-5" aria-busy={s.pending}>
        <FormError message={s.error} />
        <AuthField
          label="Name"
          icon={<User size={16} />}
          autoComplete="name"
          placeholder="Ada Lovelace"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <AuthField
          label="Work email"
          icon={<Mail size={16} />}
          type="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          placeholder="you@company.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <PasswordField
          label="Password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={
            <span className={cx("inline-flex items-center gap-1.5 transition-colors", longEnough && "text-good")}>
              {longEnough ? <Check size={13} strokeWidth={2.5} aria-hidden /> : <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />}
              At least {MIN_PASSWORD} characters
            </span>
          }
        />
        <SubmitButton pending={s.pending} pendingLabel="Creating account…">
          Create account
        </SubmitButton>
      </form>
    </AuthFrame>
  );
}

// --- Forgotten passwords --------------------------------------------------------------------

/** How long a reset link works. Mirrors RESET_EXPIRES_IN in apps/api/src/auth-email.ts. */
const RESET_LINK_HOURS = 1;

const RESET_PANEL = { eyebrow: "Release verdicts", headline: "Find the release that’s costing you conversions, and who it hit." };

/** A link styled like the auth pages' secondary buttons. */
const secondaryLinkClass =
  "inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-control bg-surface text-[15px] font-medium text-ink transition-colors hover:bg-surface-2";

/**
 * "Forgot your password?": asks for the address and always answers the same way, so the page never
 * says whether someone has an account. The email itself says how that account signs in.
 */
export function ForgotPassword() {
  const location = useLocation();
  const [email, setEmail] = useState(() => (location.state as { email?: string } | null)?.email ?? "");
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const res = await authClient
      .requestPasswordReset({ email, redirectTo: "/reset-password" })
      .catch(() => ({ error: { status: 0, message: "Can't reach the server. Check your connection and try again." } }));
    setPending(false);
    if (res.error) {
      const status = (res.error as { status?: number }).status;
      return setError(status === 429 ? "That's a lot of requests. Wait a minute and try again." : (res.error.message ?? "Something went wrong on our side. Please try again in a moment."));
    }
    track("password_reset_requested");
    setSent(true);
  };

  if (sent)
    return (
      <AuthFrame
        title="Check your email"
        focusTitle
        subtitle={
          <>
            If there's a Metrickle account for <strong className="font-medium break-all text-ink">{email}</strong>, we've sent it a link to choose a new password.
          </>
        }
        footer={
          <Link className={linkClass} to="/login">
            Back to sign in
          </Link>
        }
        panel={RESET_PANEL}
      >
        <div className="space-y-4">
          <Notice icon={<Mail size={18} className="text-accent-text" />}>
            The link works for {RESET_LINK_HOURS} hour. It can take a minute to arrive, so check your spam or promotions folder too.
          </Notice>
          <p className="text-sm text-ink-2">
            If you sign in with Google, GitHub, X or single sign-on, you don't need a password: go back and use that button. The email says which way you sign in.
          </p>
          <Button type="button" variant="link" className="w-full justify-center text-sm underline-offset-4" onClick={() => setSent(false)}>
            Use a different email, or send it again
          </Button>
        </div>
      </AuthFrame>
    );

  return (
    <AuthFrame
      title="Reset your password"
      subtitle="Enter the email you use for Metrickle and we'll send you a link to choose a new password."
      footer={
        <>
          Remembered it?{" "}
          <Link className={linkClass} to="/login">
            Sign in
          </Link>
        </>
      }
      panel={RESET_PANEL}
    >
      <form onSubmit={onSubmit} className="space-y-5" aria-busy={pending}>
        <FormError message={error} />
        <AuthField
          label="Email"
          icon={<Mail size={16} />}
          type="email"
          autoComplete="email"
          inputMode="email"
          spellCheck={false}
          placeholder="you@company.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <SubmitButton pending={pending} pendingLabel="Sending…">
          Send reset link
        </SubmitButton>
      </form>
    </AuthFrame>
  );
}

/**
 * Where the emailed link lands: better-auth checks the token and adds ?token=, or ?error= when it
 * has expired or been used. A new password signs out every session, so this one too; the person
 * then signs in with it.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [expired, setExpired] = useState(!token || !!params.get("error"));
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorId = useId();
  const longEnough = password.length >= MIN_PASSWORD;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!longEnough) return setError(`Use at least ${MIN_PASSWORD} characters.`);
    setPending(true);
    setError(null);
    const res = await authClient
      .resetPassword({ newPassword: password, token: token! })
      .catch(() => ({ error: { code: "", message: "Can't reach the server. Check your connection and try again." } }));
    if (res.error) {
      setPending(false);
      if ((res.error as { code?: string }).code === "INVALID_TOKEN") return setExpired(true);
      return setError(res.error.message ?? "Something went wrong on our side. Please try again in a moment.");
    }
    track("password_reset");
    // The server has already ended every session; this clears this browser's copy too.
    await authClient.signOut().catch(() => {});
    navigate("/login?reset=1", { replace: true });
  };

  if (expired)
    return (
      <AuthFrame
        title="This link doesn't work any more"
        focusTitle
        subtitle={`Password reset links work for ${RESET_LINK_HOURS} hour and only once. Ask for a new one and we'll email it to you.`}
        footer={
          <Link className={linkClass} to="/login">
            Back to sign in
          </Link>
        }
        panel={RESET_PANEL}
      >
        <Link to="/forgot-password" className={secondaryLinkClass}>
          <Mail size={16} className="text-ink-3" />
          Email me a new link
        </Link>
      </AuthFrame>
    );

  return (
    <AuthFrame
      title="Choose a new password"
      subtitle="Saving it signs you out on every device. Then sign in with your new password."
      footer={
        <Link className={linkClass} to="/login">
          Back to sign in
        </Link>
      }
      panel={RESET_PANEL}
    >
      <form onSubmit={onSubmit} className="space-y-5" aria-busy={pending} noValidate>
        <FormError id={errorId} message={error} />
        <PasswordField
          label="New password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          hint={
            <span className={cx("inline-flex items-center gap-1.5 transition-colors", longEnough && "text-good")}>
              {longEnough ? <Check size={13} strokeWidth={2.5} aria-hidden /> : <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />}
              At least {MIN_PASSWORD} characters
            </span>
          }
        />
        <SubmitButton pending={pending} pendingLabel="Saving…">
          Save new password
        </SubmitButton>
      </form>
    </AuthFrame>
  );
}
